'use client';

/**
 * Unified card resolver — one content_key → component lookup across every surface.
 *
 * Doctrine: everything is a card, and a card is borrowed, not duplicated — the
 * SAME content_key mounts on canvas, explorer, rail, composer or chat. The five
 * surface registries (CANVAS / EXPLORER / INSIGHTS / COMPOSER / CHAT) declare
 * which cards a surface COMPOSES via its @preset; they were never meant to gate
 * which cards an agent-recommendation slot can RESOLVE. A recommendation ("the
 * answer is `home_pipeline`", "`obeya_project_list`") is surface-agnostic: it
 * names a card, the slot mounts it. Limiting that resolution to one surface's
 * registry forces hand-borrowing (obeya_project_list copied explorer→canvas) and
 * silently drops any of the ~170 strategies whose renderer_card lives elsewhere.
 *
 * **Lazy by necessity.** A canvas card (e.g. the reception renderer slot) is
 * itself imported by CANVAS_CARD_REGISTRY, and it imports this module — a
 * cycle. Spreading the registries into a merged object at module-eval time
 * reads CANVAS_CARD_REGISTRY before its `const` initialises (TDZ). So the
 * lookup is deferred to call time (render), by which point every surface
 * registry is fully evaluated. Resolution checks registries in precedence
 * order (canvas first) — content keys are unique per card, so order only
 * matters in the impossible-collision case.
 *
 * @layer presentation
 * @arch_component_type registry
 * @arch_domain cards
 */

import { type ComponentType } from 'react';
import { CANVAS_CARD_REGISTRY } from '@/components/canvas/cards/card-registry';
import { EXPLORER_CARD_REGISTRY } from '@/components/explorer/cards/card-registry';
import { INSIGHTS_CARD_REGISTRY } from '@/components/copilot/rail-insights/card-registry';
import { COMPOSER_CARD_REGISTRY } from '@/components/copilot/composer/composer-card-registry';
import { CHAT_CARD_REGISTRY } from '@/components/copilot/chat/chat-card-registry';

/**
 * Resolve a content_key to its card component across all surfaces, or undefined
 * if no surface registered it. Surface-agnostic; cards are typed bare
 * (React.ComponentType), so by-reference mounts pass optional props (`entityId`,
 * …) which entity-free cards ignore harmlessly.
 *
 * Registries are referenced inside the function (call time, not module eval) to
 * sidestep the canvas-registry ⇄ slot import cycle — see the lazy note above.
 */
export function resolveCard(key: string): ComponentType<any> | undefined {
  const registries: Array<Record<string, ComponentType<any>>> = [
    CANVAS_CARD_REGISTRY,
    EXPLORER_CARD_REGISTRY,
    INSIGHTS_CARD_REGISTRY,
    COMPOSER_CARD_REGISTRY,
    CHAT_CARD_REGISTRY,
  ];
  for (const registry of registries) {
    const card = registry[key];
    if (card) return card;
  }
  return undefined;
}
